import { oids } from "@/configs/Settings";
import { fhirClients } from "src-next/shared/api/fhirClient";

import {
  CRM_CONTRACT_IDENTIFIER_ALL,
  CRM_CONTRACT_IDENTIFIER_PAID,
  CRM_CONTRACT_IDENTIFIER_TRIAL,
} from "../constants/crmContractTiers";

/**
 * CRM Contract service (Fluxmed padrão).
 *
 * Fonte de verdade: Contract (FHIR) no contexto admin.
 * - Evita `localStorage` para “simular” contratação.
 * - Centraliza a regra de negócio “existe contrato CRM executado?”.
 *
 * Regra equivalente ao legado (`GetContract` / `CRMContractContext`):
 * - authority=Organization/<organizationId>
 * - status=executed
 * - identifier=crm20,crm50,crm100
 * - _sort=-_lastUpdated
 * - _count=1
 */

export interface GetCrmContractByOrganizationParams {
  organizationId?: string;
  /** Override do filtro FHIR `identifier` (ex.: só `crm20`). */
  identifier?: string;
}

function normalizeOrganizationId(organizationId?: string): string | null {
  const trimmed = organizationId?.trim();
  if (!trimmed) return null;
  return trimmed;
}

function extractFirstContract(bundle: fhir5.Bundle): fhir5.Contract | null {
  const firstEntry = bundle.entry?.[0]?.resource;
  if (!firstEntry) return null;
  if (firstEntry.resourceType !== "Contract") return null;
  return firstEntry as fhir5.Contract;
}

async function searchLatestCrmContract(
  organizationId: string,
  identifier: string,
): Promise<fhir5.Contract | null> {
  try {
    const bundle = await fhirClients.admin.search<fhir5.Contract>("Contract", {
      authority: `Organization/${oids.cnpj}-${organizationId}`,
      status: "executed",
      identifier,
      _sort: "-_lastUpdated",
      _count: "1",
    });

    return extractFirstContract(bundle);
  } catch (error) {
    throw error;
  }
}

async function getLatestCrmContractByOrganization(
  params: GetCrmContractByOrganizationParams,
): Promise<fhir5.Contract | null> {
  const organizationId = normalizeOrganizationId(params.organizationId);
  if (!organizationId) return null;

  const identifier = params.identifier ?? CRM_CONTRACT_IDENTIFIER_ALL;
  return searchLatestCrmContract(organizationId, identifier);
}

/** Chamada específica: último contrato tier `crm20` (trial). */
async function getLatestCrm20ContractByOrganization(
  params: GetCrmContractByOrganizationParams,
): Promise<fhir5.Contract | null> {
  const organizationId = normalizeOrganizationId(params.organizationId);
  if (!organizationId) return null;

  return searchLatestCrmContract(organizationId, CRM_CONTRACT_IDENTIFIER_TRIAL);
}

async function getLatestCrmPaidContractByOrganization(
  params: GetCrmContractByOrganizationParams,
): Promise<fhir5.Contract | null> {
  const organizationId = normalizeOrganizationId(params.organizationId);
  if (!organizationId) return null;

  return searchLatestCrmContract(organizationId, CRM_CONTRACT_IDENTIFIER_PAID);
}

async function hasAnyCrmContractByOrganization(
  params: GetCrmContractByOrganizationParams,
): Promise<boolean> {
  const contract = await getLatestCrmContractByOrganization({
    ...params,
    identifier: CRM_CONTRACT_IDENTIFIER_ALL,
  });
  return Boolean(contract?.id);
}

async function hasActiveCrmContractByOrganization(
  params: GetCrmContractByOrganizationParams,
): Promise<boolean> {
  const contract = await getLatestCrmContractByOrganization(params);
  return Boolean(contract?.id);
}

export const crmContractService = {
  getLatestCrmContractByOrganization,
  getLatestCrm20ContractByOrganization,
  getLatestCrmPaidContractByOrganization,
  hasAnyCrmContractByOrganization,
  hasActiveCrmContractByOrganization,
};
